import { isNil, toString } from 'lodash';
import {
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_CALCULATIONS_COLUMNS,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_COLUMNS,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_EXCLUDED_PUBLIC_COMPANY_ROWS_COUNT,
} from 'pages/FundInvestments/common/constants';
import { dbShortDate, getNumberValue, getObjectValue } from 'utilities';
import { ReverseParserParams } from './createReverseParser.types';
import { updateFieldValue } from './utils';

const { percentage_change: PERCENTAGE_CHANGE, weighting: WEIGHTING } =
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_COLUMNS;

const {
  as_of_date: AS_OF_DATE,
  metric_selection: METRIC_SELECTION,
  percentile_selection_a: PERCENTILE_SELECTION_A,
  percentile_selection_b: PERCENTILE_SELECTION_B,
  specified_adjustment: SPECIFIED_ADJUSTMENT,
  adjustment_selection: ADJUSTMENT_SELECTION,
  market_adjustment: MARKET_ADJUSTMENT,
} = FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_CALCULATIONS_COLUMNS;

const createReverseParser = (params: ReverseParserParams): void => {
  const {
    asOfDate,
    cells,
    fieldAttributes,
    percentileSelection,
    setMarketAdjustment,
    setMarketComparisons,
    setSynchronizedMarketAdjustment,
  } = params;

  const { percentile_selection_a: percentileSelectionA, percentile_selection_b: percentileSelectionB } =
    getObjectValue(percentileSelection);

  Object.values(cells).forEach(cell => {
    const { data, y: colIndex, customKey, value, x: rowIndex } = getObjectValue(cell);
    const { capIqId, order } = getObjectValue(data);

    const colNumber = getNumberValue(colIndex) + 1;
    const rowNumber = getNumberValue(rowIndex) + 1;
    const comparisonOrder =
      rowNumber - FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_EXCLUDED_PUBLIC_COMPANY_ROWS_COUNT;

    // Comparisons
    if (!isNil(capIqId)) {
      // If order has changed, update it
      if (comparisonOrder !== order) {
        updateFieldValue({
          category: 'comparison',
          cell,
          comparisonOrder,
          field: 'order',
          fieldAttributes,
          percentileSelection,
          setMarketAdjustment,
          setMarketComparisons,
          setSynchronizedMarketAdjustment,
        });
      }

      switch (colNumber) {
        case PERCENTAGE_CHANGE.column:
          updateFieldValue({
            category: 'comparison',
            cell,
            comparisonOrder,
            field: 'enabled',
            fieldAttributes,
            percentileSelection,
            setMarketAdjustment,
            setMarketComparisons,
            setSynchronizedMarketAdjustment,
          });
          break;

        case WEIGHTING.column:
          updateFieldValue({
            category: 'comparison',
            cell,
            comparisonOrder,
            field: 'weight',
            fieldAttributes,
            percentileSelection,
            setMarketAdjustment,
            setMarketComparisons,
            setSynchronizedMarketAdjustment,
          });
          break;

        default:
          break;
      }
    }

    // Market Adjustment
    switch (customKey) {
      case AS_OF_DATE.key:
        if (dbShortDate(asOfDate) !== dbShortDate(toString(value))) {
          updateFieldValue({
            category: 'market_adjustment',
            cell,
            comparisonOrder,
            field: 'as_of_date',
            fieldAttributes,
            percentileSelection,
            setMarketAdjustment,
            setMarketComparisons,
            setSynchronizedMarketAdjustment,
          });
        }
        break;

      case METRIC_SELECTION.key:
        updateFieldValue({
          category: 'market_adjustment',
          cell,
          comparisonOrder,
          field: 'metric_selection',
          fieldAttributes,
          percentileSelection,
          setMarketAdjustment,
          setMarketComparisons,
          setSynchronizedMarketAdjustment,
        });
        break;

      case PERCENTILE_SELECTION_A.optionKey:
        if (percentileSelectionA !== value) {
          updateFieldValue({
            category: 'percentile',
            cell,
            comparisonOrder,
            field: 'percentile_selection_a',
            fieldAttributes,
            percentileSelection,
            setMarketAdjustment,
            setMarketComparisons,
            setSynchronizedMarketAdjustment,
          });
        }
        break;

      case PERCENTILE_SELECTION_B.optionKey:
        if (percentileSelectionB !== value) {
          updateFieldValue({
            category: 'percentile',
            cell,
            comparisonOrder,
            field: 'percentile_selection_b',
            fieldAttributes,
            percentileSelection,
            setMarketAdjustment,
            setMarketComparisons,
            setSynchronizedMarketAdjustment,
          });
        }
        break;

      case SPECIFIED_ADJUSTMENT.key:
        updateFieldValue({
          category: 'market_adjustment',
          cell,
          comparisonOrder,
          field: 'specified_adjustment',
          fieldAttributes,
          percentileSelection,
          setMarketAdjustment,
          setMarketComparisons,
          setSynchronizedMarketAdjustment,
        });
        break;

      case ADJUSTMENT_SELECTION.key:
        updateFieldValue({
          category: 'market_adjustment',
          cell,
          comparisonOrder,
          field: 'adjustment_selection',
          fieldAttributes,
          percentileSelection,
          setMarketAdjustment,
          setMarketComparisons,
          setSynchronizedMarketAdjustment,
        });
        break;

      case MARKET_ADJUSTMENT.key:
        updateFieldValue({
          category: 'market_adjustment',
          cell,
          comparisonOrder,
          field: 'market_adjustment',
          fieldAttributes,
          percentileSelection,
          setMarketAdjustment,
          setMarketComparisons,
          setSynchronizedMarketAdjustment,
        });
        break;

      default:
        break;
    }
  });
};

export default createReverseParser;
